// PORTED FROM apps/web-employer/src/components/applicant-row.tsx — kept in sync.
// Admin is concierge: it drives the SAME "Mời phỏng vấn" interview-slot flow on
// behalf of the org (BE authorizes ADMIN cross-org). Differences vs employer:
// admin-namespaced query keys, admin reject endpoint, and mode="admin" profile.

import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ApplicationStateBadge,
  Avatar,
  Button,
  ConfirmDialog,
  useToast,
} from "@chotca/ui";
import { Eye, Trash2, UserPlus, X as XIcon } from "lucide-react";
import { maskFullName, maskPhone, type ApplicationState } from "@chotca/config";
import { getSdk } from "@chotca/api-client";
import { qk } from "@chotca/api-client";
import { WorkerProfileModal } from "@/components/worker-profile-modal";
import { InviteApplicantDialog } from "@/components/invite-applicant-dialog";

type Applicant = {
  application: {
    id: string;
    state: string;
    appliedAt: string;
    note?: string | null;
  };
  worker: {
    userId: string;
    fullName: string;
    avatarUrl?: string | null;
    currentDistrict?: string | null;
    kycVerifiedAt?: string | null;
    ratingAvg: number | null;
    ratingCount: number;
  };
  invited: boolean;
  committedAt: string | null;
  contact: { phone: string | null; email: string | null } | null;
};

export function AdminApplicantRow({
  applicant,
  postingId,
  orgId,
  postingActive = true,
}: {
  applicant: Applicant;
  postingId: string;
  orgId: string;
  /**
   * False when the posting is CLOSED / EXPIRED / CANCELLED. Hides invite &
   * reject actions and locks profile browsing for not-yet-invited applicants.
   * Already-invited applicants stay viewable regardless.
   */
  postingActive?: boolean;
}) {
  // `invited` = an invite has been sent. `contact` only arrives once the invite
  // commits (worker picks a slot), so it can't stand in for "invited" — a
  // freshly-invited applicant has invited=true but no contact yet.
  const invited = applicant.invited;
  const canBrowseProfile = postingActive || invited;
  const canAct = postingActive && !invited;
  // List stays masked for every applicant — full identity (real name, contact)
  // is revealed only inside the profile modal ("Xem chi tiết"). Admin still
  // reads everything once the detail is open (BE profile endpoint = revealAll).
  const maskedName = maskFullName(applicant.worker.fullName);
  const qc = useQueryClient();
  const { toast } = useToast();
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [confirmKind, setConfirmKind] = React.useState<
    "reject" | "delete" | null
  >(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: qk.admin.applicants(postingId) });
    qc.invalidateQueries({
      queryKey: qk.admin.applicantProfile(applicant.application.id),
    });
  }

  const reject = useMutation({
    mutationFn: () =>
      getSdk().admin.rejectApplication(applicant.application.id),
    onSuccess: () => {
      invalidate();
      toast({ tone: "success", title: "Đã từ chối ứng viên" });
      setConfirmKind(null);
      setProfileOpen(false);
    },
    onError: () => toast({ tone: "error", title: "Không từ chối được" }),
  });

  const remove = useMutation({
    mutationFn: () =>
      getSdk().admin.deleteApplication(applicant.application.id),
    onSuccess: () => {
      invalidate();
      toast({ tone: "success", title: "Đã xoá hồ sơ ứng tuyển" });
      setConfirmKind(null);
      setProfileOpen(false);
    },
    onError: () => toast({ tone: "error", title: "Không xoá được" }),
  });

  return (
    <>
      <div className="rounded-2xl border border-smoke bg-paper p-4 transition-colors hover:border-ink/20">
        <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-start sm:justify-between">
          <button
            type="button"
            onClick={() => canBrowseProfile && setProfileOpen(true)}
            disabled={!canBrowseProfile}
            className="flex flex-1 items-start gap-3 text-left transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-100"
          >
            <Avatar
              name={maskedName}
              src={applicant.worker.avatarUrl}
              size="md"
              tone="ink"
              className="h-12 w-12 text-sm"
            />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-ink">{maskedName}</p>
                {applicant.worker.kycVerifiedAt ? (
                  <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">
                    ✓ CCCD
                  </span>
                ) : null}
                <ApplicationStateBadge
                  state={applicant.application.state as ApplicationState}
                />
              </div>
              <p className="mt-0.5 text-xs text-ash">
                {applicant.worker.currentDistrict ?? "—"} ·{" "}
                {applicant.worker.ratingAvg !== null
                  ? `⭐ ${applicant.worker.ratingAvg.toFixed(1)} (${applicant.worker.ratingCount})`
                  : "Chưa có đánh giá"}
              </p>
              {applicant.application.note ? (
                <p className="mt-1 line-clamp-2 text-xs italic text-ink/70">
                  "{applicant.application.note}"
                </p>
              ) : null}
            </div>
          </button>

          <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
            {/* Contact stays masked in the list. The full number, email and the
                "Mở Zalo" action live inside the profile modal ("Xem chi tiết"),
                so the list reads as a masked roster until admin drills in. */}
            {applicant.contact ? (
              <div className="text-left text-xs sm:text-right">
                {applicant.contact.phone ? (
                  <p className="font-mono text-sm font-medium text-ink/40">
                    {maskPhone(applicant.contact.phone)}
                  </p>
                ) : null}
                <p className="text-[11px] text-ash">
                  Xem chi tiết để hiện liên hệ
                </p>
              </div>
            ) : null}

            {canAct ? (
              <div className="flex flex-wrap gap-2 sm:justify-end">
                <Button
                  variant="ghost"
                  size="iconSm"
                  onClick={() => setProfileOpen(true)}
                  aria-label="Xem hồ sơ"
                  title="Xem hồ sơ"
                  className="transition-colors hover:bg-mist"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={() => setInviteOpen(true)}
                  disabled={reject.isPending}
                >
                  <UserPlus className="h-4 w-4" />
                  Mời PV
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmKind("reject")}
                  loading={reject.isPending}
                >
                  <XIcon className="h-4 w-4" />
                  Từ chối
                </Button>
              </div>
            ) : invited ? (
              <div className="text-left text-xs sm:text-right">
                <p className="font-semibold text-ink">
                  {applicant.committedAt
                    ? "Đã chốt lịch phỏng vấn"
                    : "Đã mời phỏng vấn"}
                </p>
                {!applicant.committedAt ? (
                  <p className="text-ash">Chờ ứng viên xác nhận chốt lịch.</p>
                ) : null}
                <Button
                  variant="ghost"
                  size="iconSm"
                  onClick={() => setProfileOpen(true)}
                  aria-label="Xem hồ sơ"
                  title="Xem hồ sơ"
                  className="mt-1 transition-colors hover:bg-mist"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
            ) : !postingActive ? (
              <p className="text-xs text-ash">Tin đã đóng</p>
            ) : null}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmKind("delete")}
              loading={remove.isPending}
              className="text-danger hover:bg-danger/5"
            >
              <Trash2 className="h-4 w-4" />
              Xoá hồ sơ
            </Button>
          </div>
        </div>
      </div>

      <WorkerProfileModal
        applicationId={applicant.application.id}
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onInvite={() => {
          setProfileOpen(false);
          setInviteOpen(true);
        }}
        onReject={() => setConfirmKind("reject")}
        invitePending={false}
        rejectPending={reject.isPending}
        mode="admin"
        inviteLabel="Mời phỏng vấn"
      />

      <InviteApplicantDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={invalidate}
        orgId={orgId}
        applicationId={applicant.application.id}
        workerName={applicant.worker.fullName}
      />

      <ConfirmDialog
        open={confirmKind === "reject"}
        onClose={() => setConfirmKind(null)}
        onConfirm={() => reject.mutate()}
        title="Từ chối ứng viên?"
        description="Hồ sơ sẽ chuyển sang trạng thái REJECTED."
        confirmLabel="Từ chối"
        destructive
        loading={reject.isPending}
      />

      <ConfirmDialog
        open={confirmKind === "delete"}
        onClose={() => setConfirmKind(null)}
        onConfirm={() => remove.mutate()}
        title="Xoá hồ sơ ứng tuyển?"
        description="Xoá hẳn record ứng tuyển này khỏi tin, không thể khôi phục. Nếu đang giữ lượt phỏng vấn, lượt sẽ được hoàn lại cho nhà tuyển dụng."
        confirmLabel="Xoá"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}
